import type { ReactNode } from 'react';
import type { TopNavTabsProps } from './top-nav-tabs';

import { useRef, useState, useLayoutEffect } from 'react';

import { Box } from '@mui/material';

import { TopNavTabs } from './top-nav-tabs';

// ----------------------------------------------------------------------

export type TopNavLayoutProps = TopNavTabsProps & {
  logo: ReactNode;
  /** Right side of the header, e.g. buttons and the account menu. */
  actions?: ReactNode;
  /** Drawn behind the header and the content, e.g. a glow. */
  backdrop?: ReactNode;
  children: ReactNode;
  /** Leaves out the tab bar, for a page whose own back button leads out. */
  hideTabs?: boolean;
  'data-testid'?: string;
};

/** A dashboard shell: logo and actions on top, the tab bar under them, then the page. */
export function TopNavLayout({
  logo,
  actions,
  backdrop,
  children,
  hideTabs = false,
  'data-testid': testId,
  ...tabs
}: TopNavLayoutProps) {
  // The header's height varies with breakpoint, so page content can't hardcode
  // the offset it pins itself at — measure it and publish it as a CSS variable.
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // Floor, not offsetHeight: the header's real height is fractional, and a
    // rounded-up offset pins the bar below it a sub-pixel too low, leaving a
    // hairline of scrolling content between the two. Flooring overlaps them.
    const measure = () => setHeaderHeight(Math.floor(el.getBoundingClientRect().height));
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      data-testid={testId}
      sx={(theme) => ({
        minHeight: '100dvh',
        bgcolor: theme.palette.grey[950],
        position: 'relative',
        // clip, not hidden: `hidden` makes this a scroll container, which both
        // swallows the page scroll and stops any sticky descendant sticking.
        // `clip` contains the glow's horizontal bleed without either effect.
        overflowX: 'clip',
        // Published for page content that pins itself under the header.
        '--top-nav-h': `${headerHeight}px`,
      })}
    >
      {backdrop}

      {/* Logo row and navigation pin to the top as one block. Opaque, so page
          content scrolls behind it rather than showing through. */}
      <Box
        ref={headerRef}
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          bgcolor: theme.palette.grey[950],
        })}
      >
        <Box
          component="header"
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 4, md: 6 },
            pt: { xs: 2, sm: 3 },
          }}
        >
          {logo}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>{actions}</Box>
        </Box>

        {!hideTabs && <TopNavTabs {...tabs} />}
      </Box>

      <Box
        component="main"
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, md: 5, lg: 10 },
          pt: hideTabs ? { xs: 2, sm: 3 } : 0,
          pb: { xs: 3, md: 5, lg: 6 },
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
